// Puppet-import naming / classification rules (Order — PSD/SVG puppet import).
//
// PURE, format-independent logic shared by both readers (the ag-psd PSD reader
// and the XML/DOM SVG reader) and the importer tests. Encodes the kickoff's
// reading rules so PSD and SVG normalize to one rig structure:
//   - '+' prefix  → independent part (own pivot + keyable transform);
//   - a group is a rig NODE only if it is a '+' part or a Character-Animator
//     convention-named group — everything else (generic auto-nest:  <Path>,
//     'Layer 7', 'Wings copy 5', 'Group 3', adjacent unnamed) collapses into its
//     named parent's flattened artwork;
//   - Mouth / Left Eye / Right Eye are DISCRETE SWAP CHANNELS whose named
//     children are mutually-exclusive variants;
//   - viseme coverage is reported against the standard CH viseme set.

import { CH_VISEMES, type Viseme, type SwapRole } from './layer';

// Character Animator convention names: a group carrying one of these is a rig
// node even without a '+'. Matched case-insensitively against the display name
// (after the leading '+' is stripped). This mirrors how CH coalesces artwork —
// only conventionally-named groups (and '+' parts) become independent layers.
export const CH_CONVENTION_NAMES: string[] = [
  'Head', 'Body', 'Torso', 'Neck', 'Hair', 'Jaw',
  'Mouth', 'Nose',
  'Eyes', 'Eye', 'Left Eye', 'Right Eye', 'Eyeball', 'Pupil', 'Blink',
  'Eyebrow', 'Eyebrows', 'Brow', 'Brows', 'Left Eyebrow', 'Right Eyebrow',
  'Ear', 'Left Ear', 'Right Ear',
  'Arm', 'Left Arm', 'Right Arm', 'Hand', 'Left Hand', 'Right Hand',
  'Leg', 'Left Leg', 'Right Leg', 'Foot', 'Left Foot', 'Right Foot',
  'Wing', 'Wings', 'Tail',
];

const CONVENTION_SET = new Set(CH_CONVENTION_NAMES.map(n => n.toLowerCase()));

// Canonicalize a name for tolerant matching: lowercase, and treat '_' as a word
// separator (Illustrator/Photoshop layers vary between 'Left Eye', 'Left_Eye',
// and 'Left  Eye'). This is why convention/swap matching survives the NK library's
// mixed spacing without per-file special-casing.
function canonName(s: string): string {
  return (s ?? '').trim().toLowerCase().replace(/_+/g, ' ').replace(/\s+/g, ' ');
}

// Strip a leading '+' (and surrounding whitespace) from a raw part name. Records
// whether the '+' was present (→ independent part). The raw name comes from the
// PSD layer name or the SVG `data-name` attribute — NEVER the SVG `id` (which
// Illustrator hex-escapes); the caller is responsible for passing data-name.
export function stripPlus(rawName: string): { display: string; independent: boolean } {
  const trimmed = (rawName ?? '').trim();
  if (trimmed.startsWith('+')) {
    return { display: trimmed.slice(1).trim(), independent: true };
  }
  return { display: trimmed, independent: false };
}

// True when `display` (already '+'-stripped) is a Character Animator convention
// name. Generic auto-nest names ('<Path>', 'Layer 7', 'Wings copy 5', 'Group')
// are not convention names and (unless '+'-marked) collapse into artwork.
export function isConventionName(display: string): boolean {
  return CONVENTION_SET.has(canonName(display));
}

// A group becomes a rig node iff it is a '+' part or a convention-named group.
// `independent` is the result of stripPlus().independent.
export function isRigNode(display: string, independent: boolean): boolean {
  return independent || isConventionName(display);
}

// Generic auto-generated names that an importer should treat as artwork, never
// as a node — used by readers to skip naming a collapsed group and by tests.
// Covers Illustrator's `<Path>` / `<Group>` / `<Compound Path>` / `<Clip Group>`
// and Photoshop's 'Layer N' plus the universal 'X copy N' duplication suffix.
const GENERIC_RE =
  /^(<[^>]*>|layer\s*\d+|group(\s*\d+)?|path(\s*\d+)?|compound\s*path.*|clip\s*group.*|.*\bcopy(\s*\d+)?)$/i;

export function isGenericName(name: string): boolean {
  const { display } = stripPlus(name);
  if (!display) return true; // unnamed / blank → generic
  if (isConventionName(display)) return false;
  return GENERIC_RE.test(display.trim());
}

// Classify a swap channel from its (stripped) display name. Mouth → 'mouth';
// 'Left Eye' / 'Right Eye' → eye-left / eye-right; a bare 'Eye'/'Eyes' → 'eye'.
// Everything else → 'none' (an ordinary single-look part, not a swap channel).
export function swapRoleFor(display: string): SwapRole {
  const n = canonName(display);
  if (n === 'mouth') return 'mouth';
  if (n === 'left eye') return 'eye-left';
  if (n === 'right eye') return 'eye-right';
  if (n === 'eye') return 'eye';
  // 'Eyes' (plural) is a CONTAINER grouping Left/Right Eye, not a swap channel.
  return 'none';
}

// The character-POV side a part name carries (Left/Right), tolerating '_' / '-'
// separators and a trailing L/R token. Returns null when the name carries no
// side. Mirrors grammarRoleFor's side reading so pupil/eye assembly and grammar
// role classification agree on which side a part belongs to.
export function sideOf(name: string): 'left' | 'right' | null {
  const n = canonName(stripPlus(name).display).replace(/-+/g, ' ');
  if (/\bleft\b|\bl\b/.test(n)) return 'left';
  if (/\bright\b|\br\b/.test(n)) return 'right';
  return null;
}

// A clean, single-layer PUPIL part name — the importer hoists these out of the
// eye as separate parts so the grammar can slide them (pupil-left / pupil-right).
// Excludes the 'Pupil Range' slide-guide and any ' + '-joined merged layer
// (Illustrator/Photoshop flattened several named layers into one — not a clean
// part, so we must not treat the blob as a movable pupil).
export function isPupilPartName(display: string): boolean {
  const n = canonName(display);
  return /\bpupil\b/.test(n) && !/\brange\b/.test(n) && !n.includes(' + ');
}

// True when a '+'-stripped display name is a per-side EYE sub-part — a pupil,
// blink, (eye)lid, iris, eyeball, eye white/skin/shine, or liner — as opposed to
// the eye group itself, an eyebrow, or an unrelated part. The importer uses this
// to ASSEMBLE Left/Right Eye swap channels from flat siblings when the source
// carries no explicit 'Left Eye' / 'Right Eye' wrapper group. Excludes brows
// (their own parts) and ' + '-merged multi-name layers (flattened → not clean).
export function isEyePartName(display: string): boolean {
  const n = canonName(display);
  if (!n) return false;
  if (n.includes(' + ')) return false;              // merged / flattened
  if (/\bbrow\b|\beyebrow\b/.test(n)) return false; // brows are separate parts
  if (/\bpupil\b/.test(n)) return !/\brange\b/.test(n);
  return /\bblink\b|\beyelid\b|\blid\b|\biris\b|\beyeball\b|\bwink\b|\bliner\b|\beye\s+(white|skin|shine)\b/.test(n);
}

// Canonical viseme alias table → the CH viseme id. Case-insensitive; tolerates
// the common spelling variants of W-Oo. Returns null for a non-viseme name.
const VISEME_ALIASES: Record<string, Viseme> = (() => {
  const m: Record<string, Viseme> = {};
  for (const v of CH_VISEMES) m[v.toLowerCase()] = v;
  // W-Oo spelling variants.
  m['woo'] = 'W-Oo'; m['w-oo'] = 'W-Oo'; m['w_oo'] = 'W-Oo'; m['w oo'] = 'W-Oo';
  // Common synonyms seen in CH puppets.
  m['ee'] = 'E'; m['oo'] = 'W-Oo'; m['rest'] = 'Neutral'; m['default'] = 'Neutral';
  // Near-miss vowel spellings seen in the NK library: Ah/Aah → Aa, O/Ohh → Oh,
  // Uhh → Uh. Collision-free (no puppet carries both a near-miss and its target).
  m['ah'] = 'Aa'; m['aah'] = 'Aa'; m['o'] = 'Oh'; m['ohh'] = 'Oh'; m['uhh'] = 'Uh';
  return m;
})();

export function normalizeViseme(name: string): Viseme | null {
  const { display } = stripPlus(name);
  const key = display.trim().toLowerCase();
  return VISEME_ALIASES[key] ?? null;
}

export interface VisemeCoverage {
  present: Viseme[];   // in CH order
  missing: Viseme[];   // in CH order
  count: number;       // present.length
  total: number;       // CH_VISEMES.length
  complete: boolean;   // count === total
  // Names that looked like mouth variants but didn't map to a CH viseme.
  unrecognized: string[];
}

// Compute viseme coverage from the raw names of a Mouth swap channel's variants.
export function visemeCoverage(variantNames: string[]): VisemeCoverage {
  const present = new Set<Viseme>();
  const unrecognized: string[] = [];
  for (const raw of variantNames) {
    const v = normalizeViseme(raw);
    if (v) present.add(v);
    else unrecognized.push(stripPlus(raw).display);
  }
  const presentOrdered = CH_VISEMES.filter(v => present.has(v));
  const missingOrdered = CH_VISEMES.filter(v => !present.has(v));
  return {
    present: presentOrdered,
    missing: missingOrdered,
    count: presentOrdered.length,
    total: CH_VISEMES.length,
    complete: presentOrdered.length === CH_VISEMES.length,
    unrecognized,
  };
}

// ---- Rhubarb → CharAnim viseme bridge (lip-sync) ---------------------------
//
// Rhubarb Lip Sync emits one of nine mouth SHAPES per cue (A–H, X). Our puppets
// carry CharAnim viseme variant ids (the CH_VISEMES set). A `viseme_set` is the
// reusable table that bridges the two: it maps each Rhubarb shape to the CharAnim
// variant the lip-sync pass should swap the mouth to. One table is reused across
// every puppet because the CharAnim naming is consistent (Order 127, Matt
// 2026-06-15). Tune the map against real audio; the emitted keyframes are always
// hand-editable, nothing locks.

// Rhubarb's mouth-shape vocabulary (extended set: --extendedShapes GHX, default).
export const RHUBARB_SHAPES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'X'] as const;
export type RhubarbShape = typeof RHUBARB_SHAPES[number];

export interface VisemeSet {
  id: string;
  name: string;
  // Rhubarb shape → CharAnim viseme variant id. Every shape maps to a viseme.
  map: Record<RhubarbShape, Viseme>;
}

// The default Rhubarb→CharAnim table (Order 127 locked starting map). CharAnim
// D, R, S, Uh are intentionally NOT targeted by auto-sync — they're left for
// manual emphasis. Re-tune A–H/X here, not per puppet.
export const DEFAULT_VISEME_SET: VisemeSet = {
  id: 'charanim-default',
  name: 'CharAnim default (Rhubarb A–H/X → CH visemes)',
  map: {
    A: 'M',      // closed (m/b/p)
    B: 'E',      // slightly open (consonants, EE)  — CharAnim "Ee"
    C: 'Aa',     // open (eh/ah)
    D: 'Aa',     // wide open (aa)
    E: 'Oh',     // rounded (oh)
    F: 'W-Oo',   // puckered (oo/w)
    G: 'F',      // upper teeth on lower lip (f/v)
    H: 'L',      // tongue raised (L)
    X: 'Neutral',// idle / rest
  },
};

// ---- Flattened-input detection + export guidance (Order 127, Matt 2026-06-18)
//
// A raw .ai file and an SVG exported with the wrong settings both arrive as
// nameless geometry: Illustrator only writes layer/group names to SVG `data-name`
// when exported with Object IDs = Layer Names + Styling = Presentation Attributes.
// The importer must DETECT this (no recognizable rig parts) and report the exact
// export settings, instead of silently importing a useless one-blob rig.

// A part is "recognized" rig structure iff it would be a rig node on its own —
// a '+' independent part or a CharAnim convention-named group (Head/Body/Mouth/
// Eye/…). A flattened import yields zero recognized parts.
export function isRecognizedPart(display: string, independent: boolean): boolean {
  return isRigNode(display, independent);
}

export const SVG_EXPORT_GUIDANCE =
  'This file imported with no recognizable rig structure — every part collapsed ' +
  'into nameless geometry. Adobe Illustrator only preserves your layer/group ' +
  'names as SVG `data-name` attributes when you export with the right settings. ' +
  'Re-export from Illustrator:\n' +
  '\n' +
  '  File ▸ Export ▸ Export As… ▸ Format: SVG\n' +
  '  In the SVG Options dialog:\n' +
  '    • Styling:    Presentation Attributes   (NOT Inline / Internal CSS)\n' +
  '    • Object IDs: Layer Names                (this is what writes the part names)\n' +
  '    • Decimal:    3\n' +
  '    • Minify:     OFF\n' +
  '    • Responsive: optional\n' +
  '\n' +
  'Every part — and the Mouth group with its viseme sublayers — must be a NAMED ' +
  'layer/group before exporting. Then drop the .svg in Channels/marionette/' +
  '_import-test/ and re-run the import.';

export const AI_IMPORT_GUIDANCE =
  'A raw .ai (Adobe Illustrator) file can\'t be imported as a rig. Illustrator ' +
  'stores its layer/group hierarchy in a private PGF stream that no open SVG/PDF ' +
  'parser can read — opened as PDF the artwork flattens into nameless paths and ' +
  'every part name is lost. Export to SVG instead:\n\n' + SVG_EXPORT_GUIDANCE;
